import React from "react";
import { FormattedMessage, FormattedNumber } from "react-intl";
import { Link } from "react-router-dom";
import { UncontrolledTooltip } from "reactstrap";
import Alert from "reactstrap/lib/Alert";
import { useAccessible } from "src/authentication/hooks/useAccessible";
import { useMerchantGroupQueryParam } from "src/merchantgroups/hooks/MerchantGroupQueryParamHooks";
import { MerchantAccessRight } from "src/merchants/enums/MerchantAccessRight";
import {
  MerchantNumberStatus,
  MerchantStatus,
  MerchantSubscribers,
} from "src/merchants/formatters/MerchantFormatters";
import { MerchantSuspended } from "src/merchants/formatters/MerchantSuspended";
import { useMerchantDetailViewPathHandler } from "src/merchants/hooks/useMerchantPathHandlers";
import type { Merchant } from "src/merchants/objects/Merchant";
import {
  NumberSort,
  TemporalSort,
} from "src/shared/components/data-table/RaangeDataSortStrategies";
import { RaangeDataTable } from "src/shared/components/data-table/RaangeDataTable";
import {
  RaangeDataTableRowNumber,
  RaangeDataTableRowNumberHeader,
} from "src/shared/components/data-table/RaangeDataTableRowNumber";
import RaangeDataTableSort from "src/shared/components/data-table/RaangeDataTableSort";
import RaangeTooltip from "src/shared/components/RaangeTooltip";
import {
  ContactContactName,
  ContactCountry,
  ContactLanguage,
} from "src/shared/contacts/formatters/ContactFormatters";
import FormattedInstant from "src/shared/formatters/FormattedInstant";
import { Formatter } from "src/shared/formatters/Formatter";

export interface MerchantTableProps {
  merchants: Merchant[];
}

export const MerchantTable = ({ merchants }: MerchantTableProps) => {
  const merchantGroupQueryParam = useMerchantGroupQueryParam();
  const merchantDetailViewPathHandler = useMerchantDetailViewPathHandler();

  const canVerify = useAccessible(MerchantAccessRight.VERIFY);

  if (merchants.length === 0) {
    return (
      <Alert color="warning">
        <span className="fas fa-exclamation-circle" />{" "}
        <FormattedMessage id="merchants.empty" />
      </Alert>
    );
  }

  return (
    <RaangeDataTable<Merchant>
      className="merchant-table"
      responsive
      data={merchants}
    >
      {(processedData) => (
        <>
          <thead>
            <tr>
              <RaangeDataTableRowNumberHeader />
              <th>
                <RaangeDataTableSort<Merchant> sortKey="merchantName">
                  <FormattedMessage id="merchants.name" />
                </RaangeDataTableSort>
              </th>
              <th>
                <RaangeDataTableSort<Merchant> sortKey="merchantShortName">
                  <FormattedMessage id="merchants.shortName" />
                </RaangeDataTableSort>
              </th>
              <th>
                <RaangeDataTableSort<Merchant> sortKey="mobileVirtualNumber">
                  <FormattedMessage id="merchants.mobile_virtual_number" />
                </RaangeDataTableSort>
              </th>
              <th>
                <RaangeDataTableSort<Merchant>
                  sortKey="subscriberCount"
                  sortStrategy={NumberSort}
                >
                  <FormattedMessage id="merchants.subscriber_count" />
                </RaangeDataTableSort>
              </th>
              <th>
                <RaangeDataTableSort<Merchant>
                  sortKey="lastUpdated"
                  sortStrategy={TemporalSort}
                >
                  <FormattedMessage id="contacts.last_updated" />
                </RaangeDataTableSort>
              </th>
              <th>
                <FormattedMessage id="contacts.name" />
              </th>
              <th>
                <FormattedMessage id="contacts.language" />
              </th>
              <th>
                <FormattedMessage id="contacts.country_code" />
              </th>
              <th>
                <FormattedMessage id="merchants.status" />
              </th>
              <th>
                <FormattedMessage id="merchants.suspended" />
              </th>
              <th />
            </tr>
          </thead>

          <tbody>
            {processedData.map((merchant, index) => (
              <tr key={merchant.merchantUUID}>
                <RaangeDataTableRowNumber index={index} />
                <th>
                  <RaangeTooltip
                    tag="span"
                    tooltip={(target) => (
                      <UncontrolledTooltip placement="right" target={target}>
                        <FormattedMessage id="merchants.name.tooltip" />
                      </UncontrolledTooltip>
                    )}
                  >
                    <MerchantSubscribers
                      merchantGroup={merchantGroupQueryParam}
                    >
                      {merchant}
                    </MerchantSubscribers>
                  </RaangeTooltip>
                </th>
                <td>
                  <Formatter>{merchant.merchantShortName}</Formatter>
                </td>
                <td>
                  {canVerify && merchant.mobileVirtualNumber && (
                    <MerchantNumberStatus hideLabel>
                      {merchant}
                    </MerchantNumberStatus>
                  )}
                  <Formatter notApplicable={<FormattedMessage id="ui.na" />}>
                    {merchant.mobileVirtualNumber}
                  </Formatter>
                </td>
                <td>
                  <FormattedNumber value={merchant.subscriberCount} />
                </td>
                <td>
                  <FormattedInstant value={merchant.lastUpdated} />
                </td>
                <td>
                  <ContactContactName>{merchant}</ContactContactName>
                </td>
                <td>
                  <ContactLanguage>{merchant}</ContactLanguage>
                </td>
                <td>
                  <ContactCountry>{merchant}</ContactCountry>
                </td>
                <td>
                  <MerchantStatus>{merchant}</MerchantStatus>
                </td>
                <td>
                  <MerchantSuspended showsText={false}>
                    {merchant}
                  </MerchantSuspended>
                </td>
                <td className="raange-data-table-icons">
                  <RaangeTooltip
                    tag="span"
                    tooltip={(target) => (
                      <UncontrolledTooltip placement="right" target={target}>
                        <FormattedMessage id="merchants.detail.tooltip" />
                      </UncontrolledTooltip>
                    )}
                  >
                    <Link
                      to={merchantDetailViewPathHandler(merchant.merchantUUID)}
                    >
                      <i className="fas fa-fw fa-info-circle" />
                    </Link>
                  </RaangeTooltip>
                </td>
              </tr>
            ))}
          </tbody>
        </>
      )}
    </RaangeDataTable>
  );
};
